import React, { useState } from 'react';
import { Icon } from '../icon/Icon';
import { Tooltip } from '../tooltip/Tooltip';
import './TextArea.scss';

interface TextAreaProps {
  characterMax?: number;
  disabled?: boolean;
  htmlFor: string;
  information?: boolean | string;
  inputName?: string;
  label: string;
  notificationState?: null | 'success' | 'error' | 'warning';
  notificationMessage?: null | string;
  onInputChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  value?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  characterMax = 106,
  disabled,
  htmlFor,
  information,
  inputName,
  label,
  notificationState = '',
  notificationMessage = '',
  onInputChange,
  placeholder,
  value
}) => {
  const [characterCount, setCharacterCount] = useState(0);

  const onTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharacterCount(e.target.value.length);
    if (onInputChange) {
      return onInputChange(e);
    }
  };

  return (
    <div
      className={`ads-text-area ${disabled ? 'ads-text-area--disabled' : ''}`}
      data-testid="ads-text-area"
    >
      <label
        className="ads-text-area__label"
        htmlFor={htmlFor}
        data-testid="ads-text-area__label"
      >
        {label}
        {information && (
          <span
            className="ads-text-area__information-icon"
            data-testid="ads-text-area__icon"
          >
            <Tooltip
              content={`${information}`}
              ariaName="Text input information tooltip"
            />
          </span>
        )}
        <span className="ads-text-area__icon">
          {notificationState === 'success' && (
            <Icon
              name="checkmarkSolid"
              color="ads-color-support-success"
              size="ads-font-size-03"
            />
          )}
          {notificationState === 'error' && (
            <Icon name="errorSolid" size="ads-font-size-03" />
          )}
          {notificationState === 'warning' && (
            <Icon name="warningSolid" size="ads-font-size-03" />
          )}
        </span>
        <span className="ads-text-area__character-count">
          {characterCount}/{characterMax} characters
        </span>
      </label>
      <textarea
        className={`ads-text-area__text-area ${notificationState}`}
        maxLength={characterMax}
        aria-disabled={disabled && true}
        disabled={disabled && true}
        id={htmlFor}
        placeholder={placeholder}
        name={inputName}
        onChange={(e) => onTextareaChange(e)}
        value={value}
        data-testid="ads-text-area__text-area"
      />

      <div className={`ads-text-area__notification ${notificationState}`}>
        {notificationState === 'success' && (
          <span role="alert" data-testid="ads-text-area__notification">
            {notificationMessage}
          </span>
        )}
        {notificationState === 'error' && (
          <span role="alert" data-testid="ads-text-area__notification">
            {notificationMessage}
          </span>
        )}
        {notificationState === 'warning' && (
          <span role="alert" data-testid="ads-text-area__notification">
            {notificationMessage}
          </span>
        )}
      </div>
    </div>
  );
};
